import React, { useState, useEffect } from "react";
import "./styles.css";

const FormField = ({
                       label,
                       name,
                       type = "text",
                       value,
                       onChange,
                       placeholder = "",
                       error = "",
                       required = false,
                       className = "",
                       width = "100%",
                       isLoading = false,
                       ...rest
                   }) => {
    const [isFocused, setIsFocused] = useState(false);
    const [internalValue, setInternalValue] = useState("");
    const [numericValue, setNumericValue] = useState("");

    const formatCurrency = (number) => {
        if (number === "" || isNaN(Number(number))) return "";
        return new Intl.NumberFormat("es-CO", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(Number(number));
    };

    useEffect(() => {
        if (value !== undefined && type === "currency") {
            const rawValue = String(value).replace(/\$\s?|[^0-9.]/g, "");
            setNumericValue(rawValue);
            setInternalValue(`$ ${formatCurrency(rawValue)}`);
        } else if (value !== undefined) {
            setInternalValue(value);
        }
    }, [value, type]);

    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    const handleCurrencyChange = (e) => {
        const rawValue = e.target.value.replace(/\$\s?|(?!^-)[^0-9]/g, "");
        const sanitizedValue = rawValue.replace(/(?!^)-/g, "");

        const formattedValue = formatCurrency(sanitizedValue);

        setInternalValue(`$ ${formattedValue}`);
        setNumericValue(sanitizedValue);

        if (onChange) {
            const event = {
                ...e,
                target: {
                    ...e.target,
                    name: e.target.name,
                    value: sanitizedValue,
                },
            };
            onChange(event);
        }
    };

    const handleChange = (e) => {
        if (rest.disabled) return;

        if (type === "currency") {
            handleCurrencyChange(e);
        } else {
            setInternalValue(e.target.value);
            setNumericValue(e.target.value);
            if (onChange) onChange(e);
        }
    };

    const inputType =
        type === "date"
            ? isFocused || internalValue
                ? "date"
                : "text"
            : type;

    const isDisabled = rest.disabled;

    if (type === "hidden") {
        return (
            <input id={name} name={name} type="hidden" value={internalValue} {...rest} />
        );
    }

    const commonProps = {
        id: name,
        name: name,
        value: internalValue,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        required: required,
        className: `form-field-input ${error ? "is-invalid" : ""} ${isLoading ? "loading-skeleton" : ""}`,
        disabled: isLoading || rest.disabled,
        ...rest,
    };

    let calculatedPlaceholder = placeholder;
    if (type === "date") {
        calculatedPlaceholder = !isFocused && !internalValue ? placeholder : "";
    } else if (type === "currency") {
        calculatedPlaceholder = `$ ${placeholder}`;
    }

    const finalInputType = inputType === "currency" ? "text" : inputType;

    return (
        <div
            className={`form-field custom-form-field ${className}`}
            style={{
                width,
                opacity: isDisabled ? 0.5 : 1,
                position: type === "search" ? "relative" : undefined,
            }}
        >
            {label && (
                <label
                    htmlFor={name}
                    className="form-label form-field-label mb-0 ms-3"
                >
                    {label}
                    {required && <span className="form-field-label ms-1">*</span>}
                </label>
            )}

            {type === "textarea" ? (
                <textarea {...commonProps} placeholder={placeholder} />
            ) : type === "search" ? (
                <div className="form-field-search-wrapper">
                    <input
                        {...commonProps}
                        type="text"
                        placeholder={calculatedPlaceholder}
                        className={`${commonProps.className} form-field-search-input`}
                    />
                    <span className="search-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <circle cx="11" cy="11" r="7" stroke="#9DACBD" strokeWidth="2" />
                            <path d="M11 8C10.606 8 10.2159 8.0776 9.85195 8.22836C9.48797 8.37913 9.15726 8.6001 8.87868 8.87868C8.6001 9.15726 8.37913 9.48797 8.22836 9.85195C8.0776 10.2159 8 10.606 8 11" stroke="#9DACBD" strokeWidth="2" strokeLinecap="round" />
                            <path d="M20 20L17 17" stroke="#9DACBD" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </span>
                </div>
            ) : (
                <input
                    {...commonProps}
                    type={finalInputType}
                    placeholder={calculatedPlaceholder}
                />
            )}

            {type === "currency" && (
                <input type="hidden" name={name} value={numericValue} />
            )}

            {error && (
                <div className="invalid-feedback form-field-error">{error}</div>
            )}
        </div>
    );
};

export default FormField;
